import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Truck, Radio, CalendarDays, FileText, Building2, Users, Crown, Zap, Send, ChevronDown, ExternalLink, Copy, Search, MailPlus, Save, Sparkles, Loader2 } from "lucide-react";

const priorityStyle = (p) => {
  const v = (p || "").toLowerCase();
  if (v === "high") return "bg-[#F2635F]/10 text-[#F2635F]";
  if (v === "medium") return "bg-amber-100 text-amber-700";
  if (v === "aggregator") return "bg-slate-100 text-slate-600";
  return "bg-teal-50 text-[#0FB5A6]";
};

const STATUS_OPTIONS = ["new", "contacted", "replied", "not_interested"];
const statusStyle = (s) => ({
  new: "bg-teal-50 text-[#0FB5A6]",
  contacted: "bg-blue-50 text-blue-600",
  replied: "bg-violet-50 text-violet-600",
  invited: "bg-amber-100 text-amber-700",
  claimed: "bg-green-100 text-green-700",
  not_interested: "bg-slate-100 text-slate-400",
}[s] || "bg-teal-50 text-[#0FB5A6]");

const dmTemplate = (truck) =>
  `Hi ${truck}! I'm Brooke with FlavorFlow, a new food truck discovery and booking app launching in Northeast Wisconsin. ` +
  `We're building our Founding 100 group of local food trucks and offering free onboarding, a Founding Member badge, ` +
  `and lifetime discount pricing for early trucks. The goal is simple: help customers find you faster, help events book you ` +
  `easier, and reduce the constant "where are you today?" posting chaos. Would you be open to being listed as one of our founding trucks?`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [founding, setFounding] = useState([]);
  const [openRow, setOpenRow] = useState(null);
  const [filters, setFilters] = useState({ priority: "All", status: "All", q: "" });
  const [invitingAll, setInvitingAll] = useState(false);

  const loadFounding = () => api.get("/founding-applications").then((r) => setFounding(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {});
    loadFounding();
  }, []);

  const invite = async (f) => {
    try {
      await api.post(`/founding-applications/${f.id}/invite`, { origin_url: window.location.origin });
      toast.success(`Invite sent to ${f.email}`);
      setFounding((list) => list.map((x) => x.id === f.id ? { ...x, status: "invited" } : x));
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveProspect = async (id, patch) => {
    try {
      const { data } = await api.patch(`/founding-applications/${id}`, patch);
      setFounding((list) => list.map((x) => x.id === id ? { ...x, ...data } : x));
      toast.success("Prospect updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyDM = (f) => {
    navigator.clipboard.writeText(dmTemplate(f.truck_name));
    toast.success("Outreach message copied — paste into your DM");
  };

  const eligibleForBulk = founding.filter((f) => f.email && !f.is_aggregator && !["invited", "claimed"].includes(f.status)).length;

  const inviteAll = async () => {
    if (!eligibleForBulk) { toast.error("No prospects with an email are ready to invite yet"); return; }
    setInvitingAll(true);
    try {
      const { data } = await api.post("/founding-applications/invite-all", { origin_url: window.location.origin });
      toast.success(`Invited ${data.invited} prospect${data.invited === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      loadFounding();
    } catch (e) { toast.error(apiError(e)); }
    finally { setInvitingAll(false); }
  };

  const filtered = useMemo(() => founding.filter((f) => {
    if (filters.priority !== "All" && (f.priority || "").toLowerCase() !== filters.priority.toLowerCase()) return false;
    if (filters.status !== "All" && (f.status || "new") !== filters.status) return false;
    if (filters.q) {
      const q = filters.q.toLowerCase();
      if (!(`${f.truck_name} ${f.cuisine} ${f.city} ${f.email || ""}`.toLowerCase().includes(q))) return false;
    }
    return true;
  }), [founding, filters]);

  const cards = s ? [
    { label: "Total Trucks", value: s.trucks, icon: <Truck className="w-6 h-6" /> },
    { label: "Live Now", value: s.live_trucks, icon: <Radio className="w-6 h-6" /> },
    { label: "Founding 100", value: `${s.founding}/100`, icon: <Zap className="w-6 h-6" /> },
    { label: "Paid Owners", value: s.paid_owners, icon: <Crown className="w-6 h-6" /> },
    { label: "Events", value: s.events, icon: <CalendarDays className="w-6 h-6" /> },
    { label: "Applications", value: s.applications, icon: <FileText className="w-6 h-6" /> },
    { label: "Corporate Leads", value: s.corporate, icon: <Building2 className="w-6 h-6" /> },
    { label: "Users", value: s.users, icon: <Users className="w-6 h-6" /> },
  ] : [];

  return (
    <div className="max-w-6xl mx-auto px-5 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Admin Dashboard</h1>
      <p className="text-slate-500">Hi {user.name} — platform overview.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-teal-100 p-6 gr-shadow" data-testid={`stat-${c.label}`}>
            <div className="w-11 h-11 rounded-full bg-teal-50 text-[#0FB5A6] flex items-center justify-center">{c.icon}</div>
            <p className="font-display text-4xl font-black mt-4">{c.value}</p>
            <p className="text-sm text-slate-500 font-semibold">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Zap className="w-5 h-5 text-[#F2635F]" /> Founding 100 outreach</h2>
          <Button onClick={inviteAll} disabled={invitingAll || !eligibleForBulk} className="rounded-full font-bold bg-[#F2635F] hover:bg-[#e0554f] text-white disabled:opacity-40" data-testid="invite-all-btn">
            <MailPlus className="w-4 h-4 mr-1.5" /> {invitingAll ? "Sending…" : `Invite all with email (${eligibleForBulk})`}
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="outreach-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search truck, city, email…" className="pl-9 rounded-full" data-testid="outreach-search" />
          </div>
          <Select value={filters.priority} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v }))}>
            <SelectTrigger className="w-40 rounded-full" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>{["All", "High", "Medium", "Aggregator"].map((p) => <SelectItem key={p} value={p}>{p === "All" ? "All priorities" : p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-40 rounded-full" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>{["All", ...STATUS_OPTIONS, "invited", "claimed"].map((st) => <SelectItem key={st} value={st}>{st === "All" ? "All statuses" : st.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <p className="text-slate-400 py-8 text-center">No prospects match your filters.</p>
        ) : (
          <div className="bg-white rounded-2xl border border-teal-100 overflow-hidden" data-testid="founding-list">
            {filtered.map((f) => (
              <div key={f.id} className="border-b border-teal-50 last:border-0" data-testid={`founding-row-${f.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center px-5 py-3 text-sm gap-2 sm:gap-0">
                  <span className="w-8 font-bold text-[#0FB5A6]">{f.position}</span>
                  <span className="flex-1 font-semibold flex items-center gap-2 flex-wrap">
                    {f.truck_name}
                    {f.priority && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${priorityStyle(f.priority)}`}>{f.priority}</span>}
                    <span className="text-slate-400 font-normal">· {f.city}</span>
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize mr-2 ${statusStyle(f.status || "new")}`} data-testid={`status-badge-${f.id}`}>{(f.status || "new").replace("_", " ")}</span>
                  <span className="flex items-center gap-1.5 sm:justify-end">
                    <Button variant="ghost" onClick={() => copyDM(f)} title="Copy outreach DM" className="h-8 px-2 text-slate-500 hover:text-[#0FB5A6]" data-testid={`copy-dm-${f.id}`}><Copy className="w-4 h-4" /></Button>
                    {f.is_aggregator ? (
                      <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-xs font-bold">Partner</span>
                    ) : f.status === "claimed" ? (
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">Claimed ✓</span>
                    ) : f.status === "invited" ? (
                      <Button variant="ghost" onClick={() => invite(f)} className="rounded-full text-xs font-bold text-[#0FB5A6] h-8" data-testid={`resend-invite-${f.id}`}>Invited · resend</Button>
                    ) : (
                      <Button onClick={() => invite(f)} disabled={!f.email} title={!f.email ? "Add an email first" : ""} className="rounded-full text-xs font-bold h-8 bg-[#0FB5A6] hover:bg-[#0E9E92] text-white disabled:opacity-40" data-testid={`invite-${f.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Invite</Button>
                    )}
                    <button onClick={() => setOpenRow(openRow === f.id ? null : f.id)} className="text-slate-400 hover:text-[#0FB5A6] p-1" data-testid={`toggle-detail-${f.id}`}>
                      <ChevronDown className={`w-4 h-4 transition-transform ${openRow === f.id ? "rotate-180" : ""}`} />
                    </button>
                  </span>
                </div>
                {openRow === f.id && <ProspectEditor f={f} onSave={saveProspect} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProspectEditor({ f, onSave }) {
  const [d, setD] = useState({ owner_name: f.owner_name || "", email: f.email || "", phone: f.phone || "", status: STATUS_OPTIONS.includes(f.status) ? f.status : "new", next_step: f.next_step || "" });
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const locked = f.status === "invited" || f.status === "claimed";
  const [ai, setAi] = useState({ loading: false, text: "", channel: "dm" });
  const draft = async () => {
    setAi((a) => ({ ...a, loading: true }));
    try {
      const { data } = await api.post("/ai/outreach", { founding_id: f.id, channel: ai.channel });
      setAi((a) => ({ ...a, text: data.message }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setAi((a) => ({ ...a, loading: false })); }
  };
  const copyAi = () => { navigator.clipboard.writeText(ai.text); toast.success("Copied"); };

  return (
    <div className="px-5 pb-5 pt-1 bg-teal-50/30" data-testid={`founding-detail-${f.id}`}>
      <div className="grid sm:grid-cols-3 gap-3 mt-2">
        <Edit label="Owner name"><Input value={d.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid={`edit-owner-${f.id}`} /></Edit>
        <Edit label="Email"><Input type="email" value={d.email} onChange={(e) => set("email", e.target.value)} placeholder="add an email…" data-testid={`edit-email-${f.id}`} /></Edit>
        <Edit label="Phone"><Input value={d.phone} onChange={(e) => set("phone", e.target.value)} data-testid={`edit-phone-${f.id}`} /></Edit>
        {!locked && (
          <Edit label="Status">
            <Select value={d.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid={`edit-status-${f.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map((st) => <SelectItem key={st} value={st} className="capitalize">{st.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </Edit>
        )}
        <Edit label="Next step"><Input value={d.next_step} onChange={(e) => set("next_step", e.target.value)} data-testid={`edit-nextstep-${f.id}`} /></Edit>
        <div className="flex items-end">
          <Button onClick={() => onSave(f.id, d)} className="rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white h-10" data-testid={`save-prospect-${f.id}`}><Save className="w-4 h-4 mr-1.5" /> Save</Button>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-teal-100 p-3 bg-white" data-testid={`ai-outreach-${f.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#0FB5A6]" /> AI outreach draft</span>
          <div className="flex rounded-full bg-teal-50 p-0.5 ml-auto">
            {["dm", "email"].map((c) => (
              <button key={c} onClick={() => setAi((a) => ({ ...a, channel: c }))} className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${ai.channel === c ? "bg-[#0FB5A6] text-white" : "text-slate-500"}`} data-testid={`ai-channel-${c}-${f.id}`}>{c}</button>
            ))}
          </div>
          <Button onClick={draft} disabled={ai.loading} className="rounded-full h-8 text-xs font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid={`ai-draft-${f.id}`}>{ai.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}{ai.loading ? "Writing…" : "Generate"}</Button>
        </div>
        {ai.text && (
          <div className="mt-3">
            <Textarea value={ai.text} onChange={(e) => setAi((a) => ({ ...a, text: e.target.value }))} rows={5} className="text-sm" data-testid={`ai-text-${f.id}`} />
            <Button onClick={copyAi} variant="outline" className="mt-2 rounded-full h-8 text-xs font-bold border-teal-200" data-testid={`ai-copy-${f.id}`}><Copy className="w-3.5 h-3.5 mr-1" /> Copy message</Button>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mt-4 text-sm border-t border-teal-100 pt-3">
        <Read label="County / Area" value={f.county} />
        <Read label="Service Area" value={f.service_area} />
        <Read label="Best Contact" value={f.contact_channel} />
        <Read label="Contact / Link" value={f.contact_link} />
        {f.outreach_angle && <div className="sm:col-span-2"><Read label="Outreach Angle" value={f.outreach_angle} /></div>}
        {f.source_url && (
          <div className="sm:col-span-2">
            <a href={f.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0FB5A6] font-semibold hover:underline" data-testid={`source-link-${f.id}`}>
              Source <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

const Edit = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</Label>
    {children}
  </div>
);
const Read = ({ label, value }) => (
  <div className="py-0.5">
    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <p className="text-slate-700">{value || "—"}</p>
  </div>
);
